import { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import { Loader2, AlertCircle, ArrowRight } from 'lucide-react';

const Callback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [processingTime, setProcessingTime] = useState(0);
  const [showDebugInfo, setShowDebugInfo] = useState(false);

  useEffect(() => {
    const startTime = Date.now();
    const interval = setInterval(() => {
      setProcessingTime(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        console.log('Processing auth callback');
        console.log('URL hash:', window.location.hash);
        console.log('URL search params:', window.location.search);
        
        // Handle the OAuth or magic link response
        const { data: { session }, error: signInError } = await supabase.auth.getSession();

        if (signInError) {
          console.error('Session error:', signInError);
          throw signInError;
        }

        if (session) {
          console.log('Session found in callback, user:', session.user.id);
          
          // Get user profile
          const { data: profile, error: profileError } = await supabase
            .from('users')
            .select(`
              *,
              organizations:user_organizations(
                id,
                organization:organizations(*),
                role:user_roles(*)
              )
            `)
            .eq('id', session.user.id)
            .single();

          if (profileError && profileError.code === 'PGRST116') {
            // Profile doesn't exist, create it
            console.log('Profile not found, creating new profile');
            try {
              const { error: createError } = await supabase
                .from('users')
                .insert({
                  id: session.user.id,
                  email: session.user.email || '',
                  full_name: session.user.user_metadata?.full_name || session.user.email?.split('@')[0] || 'User'
                });

              if (createError) {
                console.error('Error creating user profile:', createError);
                // Continue anyway since the auth user was created
              }
            } catch (createError) {
              console.error('Error creating user profile:', createError);
              // Continue anyway since the auth user was created
            }
          } else if (profileError) {
            console.error('Error fetching user profile:', profileError);
            // Continue anyway since the auth user was created
          }

          // Log successful login
          try {
            await supabase.from('login_attempts').insert({
              email: session.user.email,
              ip_address: 'client',
              success: true
            });
          } catch (logError) {
            console.error('Failed to log login attempt:', logError);
          }

          // Successfully authenticated
          toast.success('Successfully signed in!');
          
          // Get the redirect path from location state or default to dashboard
          const from = location.state?.from?.pathname || '/dashboard';
          console.log('Redirecting to:', from);
          navigate(from, { replace: true });
        } else {
          // No session found
          console.error('No session found in callback');
          setError('Authentication failed. No session found.');
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        
        // Log failed attempt
        const email = new URLSearchParams(window.location.search).get('email');
        if (email) {
          try {
            await supabase.from('login_attempts').insert({
              email,
              ip_address: 'client',
              success: false,
              failure_reason: error instanceof Error ? error.message : 'Unknown error'
            });
          } catch (logError) {
            console.error('Failed to log login attempt:', logError);
          }
        }

        setError(error instanceof Error ? error.message : 'Authentication failed');
      }
    };

    // Check if we have a hash or search params in the URL (magic link/OAuth authentication)
    if (window.location.hash || window.location.search) {
      handleAuthCallback();
    } else {
      navigate('/auth/login', { replace: true });
    }
  }, [navigate, location]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
        {error ? (
          <div className="text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <div className="text-red-500 text-xl font-semibold mb-4">Authentication Error</div>
            <p className="text-gray-600 mb-4">{error}</p>
            <p className="text-gray-500 mb-6">Redirecting to login page...</p>
            
            <Link 
              to="/auth/login" 
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark"
            >
              Return to Login
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
            
            <div className="mt-6">
              <button 
                onClick={() => setShowDebugInfo(!showDebugInfo)}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                {showDebugInfo ? 'Hide' : 'Show'} Debug Info
              </button>
              
              {showDebugInfo && (
                <div className="mt-2 text-left bg-gray-50 p-3 rounded text-xs">
                  <p>URL Hash: {window.location.hash || 'None'}</p>
                  <p>URL Search: {window.location.search || 'None'}</p>
                  <p>Processing Time: {processingTime} seconds</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center">
            <Loader2 className="h-12 w-12 text-primary mx-auto animate-spin mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Verifying your authentication...
            </h2>
            <p className="text-gray-600">
              Please wait while we complete the sign-in process.
            </p>
            
            {processingTime > 10 && (
              <div className="mt-6 p-4 bg-yellow-50 rounded-md">
                <p className="text-yellow-700 text-sm">
                  This is taking longer than expected. If you continue to see this message, please try signing in again.
                </p>
                <Link 
                  to="/auth/login" 
                  className="mt-2 inline-flex items-center text-sm font-medium text-primary hover:text-primary-dark"
                >
                  Return to login
                  <ArrowRight className="ml-1 h-3 w-3" />
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Callback;